import { RepeatClockIcon } from '@chakra-ui/icons';
import { Box, Divider, HStack, Stack, VStack, useDisclosure } from '@chakra-ui/react';
import { keyframes } from '@chakra-ui/system';
import { format } from 'date-fns';
import { useRedeemTokenRewardMutation } from 'hooks/mutations/useRedeemTokenRewardMutation';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import BigTokens from 'components/BigTokens';
import Button from 'components/Button';
import GradientPopover from 'components/GradientPopover';
import GradientTooltip from 'components/GradientTooltip';
import Icon from 'components/Icon';
import { TokensModal } from 'components/Modal/TokensModal';
import ProjectIcon from 'components/ProjectIcon';
import Text from 'components/Text';
import { DATE_FORMAT, plans } from 'config/constants/plans';
import { ERoute } from 'types/frontend';

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const formatTokenCount = (n: number) => (n >= 1000 ? `${Math.round(n / 1000)}K` : `${n}`);

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const UsageBar = ({
  value,
  color,
}: {
  value: number;
  color: string;
}) => (
  <Box height="6px" bg="whiteAlpha.100" borderRadius="full" overflow="hidden">
    <Box
      width={`${value}%`}
      height="100%"
      borderRadius="full"
      bg={color}
      transition="width 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
    />
  </Box>
);

const RedeemButton = ({
  label,
  onClick,
  isLoading,
}: {
  label: string;
  onClick: () => void;
  isLoading: boolean;
  disabled?: boolean;
}) => (
  <Box
    as="button"
    onClick={onClick}
    display="flex"
    alignItems="center"
    gap="4px"
    px={2}
    py={0.5}
    borderRadius="md"
    bg="whiteAlpha.100"
    border="1px solid"
    borderColor="whiteAlpha.200"
    cursor="pointer"
    transition="all 0.15s"
    _hover={{ bg: 'whiteAlpha.200', borderColor: 'purple.400' }}
    _active={{ transform: 'scale(0.96)' }}
  >
    {isLoading ? (
      <Box
        width="40px"
        height="14px"
        borderRadius="sm"
        background="linear-gradient(90deg, transparent 0%, whiteAlpha.300 50%, transparent 100%)"
        backgroundSize="200% 100%"
        animation={`${shimmer} 1.2s ease infinite`}
      />
    ) : (
      <>
        <Icon type="Bigtokens" size={12} />
        <Text fontSize="micro" fontWeight="semibold" color="purple.300">
          {label}
        </Text>
      </>
    )}
  </Box>
);

const TabButton = ({
  label,
  icon,
  isActive,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}) => (
  <Box
    as="button"
    flex={1}
    display="flex"
    alignItems="center"
    justifyContent="center"
    gap="6px"
    py={1.5}
    borderRadius="md"
    bg={isActive ? 'whiteAlpha.200' : 'transparent'}
    cursor="pointer"
    transition="all 0.15s"
    _hover={isActive ? {} : { bg: 'whiteAlpha.100' }}
    onClick={onClick}
  >
    {icon}
    <Text
      fontSize="small"
      fontWeight={isActive ? 'semibold' : 'regular'}
      opacity={isActive ? 'primary' : 'secondary'}
    >
      {label}
    </Text>
  </Box>
);

const UserBigtokens = () => {
  const {
    tokensAvailable,
    extraTokens,
    rechargeTokens,
    nextTokenRecycleTime,
    plan,
    isFree,
    isLegacy,
    isTerminated,
    aiBaseTokensUsed,
    aiExtraTokensUsed,
    aiBaseTokenLimit,
    aiExtraTokenLimit,
    aiBasePercentUsed,
    aiExtraPercentUsed,
    storageLimitBytes,
    storageUsedBytes,
    bigTokensCollected,
  } = useCurrentUser();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const redeemMutation = useRedeemTokenRewardMutation();
  const [activeTab, setActiveTab] = useState(0);

  // Hide for all free users (including legacy) — they need to upgrade for BigToken features
  if (isFree) return null;

  const aiBaseColor =
    aiBasePercentUsed >= 90 ? 'red.400' : aiBasePercentUsed >= 70 ? 'orange.400' : 'purple.400';
  const aiExtraColor =
    aiExtraPercentUsed >= 90 ? 'red.400' : aiExtraPercentUsed >= 70 ? 'orange.400' : 'purple.400';
  const storagePercentUsed =
    storageLimitBytes > 0
      ? Math.min(100, Math.round((storageUsedBytes / storageLimitBytes) * 100))
      : 0;
  const storageColor =
    storagePercentUsed >= 90 ? 'red.400' : storagePercentUsed >= 70 ? 'orange.400' : 'purple.400';
  const hasTokens = bigTokensCollected > 0;

  const renderTokensTab = () => (
    <VStack alignItems="flex-start" spacing={3} width="100%">
      {!isTerminated && (
        <Box
          width="100%"
          p={3}
          borderRadius="lg"
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
        >
          <HStack justifyContent="space-between" mb={1}>
            <Text fontSize="small" fontWeight="semibold">
              Plan BigTokens
            </Text>
            <BigTokens amount={rechargeTokens} />
          </HStack>
          <HStack spacing={1} mt={1}>
            <Text fontSize="micro" opacity="secondary">
              Your
            </Text>
            <Text
              fontSize="micro"
              color={plans[plan].color}
              fontWeight="semibold"
              opacity="default"
            >
              {plan}
            </Text>
            <Text fontSize="micro" opacity="secondary">
              plan recharges {rechargeTokens} monthly.
            </Text>
          </HStack>
        </Box>
      )}

      {isTerminated && (
        <Box width="100%" p={3} borderRadius="lg" bg="red.900" border="1px solid" borderColor="red.700">
          <Text fontSize="small" fontWeight="semibold" color="red.300" mb={2}>
            Your subscription has ended. BigTokens no longer recharge.
          </Text>
          <Button
            fullWidth
            scheme="gradient"
            size="sm"
            onClick={() => navigate(ERoute.Plans)}
            leftIcon={<RepeatClockIcon />}
          >
            Renew subscription
          </Button>
        </Box>
      )}

      <Box
        width="100%"
        p={3}
        borderRadius="lg"
        bg="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.100"
      >
        <HStack justifyContent="space-between" mb={1}>
          <Text fontSize="small" fontWeight="semibold">
            Extra BigTokens
          </Text>
          <BigTokens amount={extraTokens} />
        </HStack>
        <Text fontSize="micro" opacity="secondary">
          Spent first. Never resets.
        </Text>
      </Box>

      {/* Current balance */}
      <Divider borderColor="whiteAlpha.100" />
      <Box
        width="100%"
        py={2.5}
        px={4}
        borderRadius="lg"
        bg="linear-gradient(135deg, rgba(159, 122, 234, 0.15) 0%, rgba(237, 137, 54, 0.10) 100%)"
        border="1px solid"
        borderColor="purple.700"
      >
        <HStack justifyContent="space-between">
          <Text fontSize="small" fontWeight="semibold">
            Current Balance
          </Text>
          <BigTokens amount={tokensAvailable} bold />
        </HStack>
      </Box>

      <Button
        fullWidth
        scheme="gradient"
        onClick={onOpen}
        leftIcon={<Icon type="Bigtokens" size={20} />}
      >
        Get Extra BigTokens
      </Button>
      <TokensModal isOpen={isOpen} onClose={onClose} />
    </VStack>
  );

  const renderUsageTab = () => (
    <VStack alignItems="flex-start" spacing={3} width="100%">
      {/* Base AI — resets monthly */}
      {!isTerminated && (
        <Box
          width="100%"
          p={3}
          borderRadius="lg"
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
        >
          <HStack justifyContent="space-between" mb={2}>
            <Text fontSize="small" fontWeight="semibold">
              Base Merlin AI
            </Text>
            <Text fontSize="micro" opacity="secondary">
              {formatTokenCount(aiBaseTokensUsed)} / {formatTokenCount(aiBaseTokenLimit)}
            </Text>
          </HStack>
          <UsageBar value={aiBasePercentUsed} color={aiBaseColor} />
          <Text fontSize="micro" opacity="tertiary" mt={1.5}>
            Resets monthly.
          </Text>
        </Box>
      )}

      {/* Extra AI — permanent, spent first, always visible */}
      {!isTerminated && (
        <Box
          width="100%"
          p={3}
          borderRadius="lg"
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
        >
          <HStack justifyContent="space-between" mb={2}>
            <Text fontSize="small" fontWeight="semibold">
              Extra Merlin AI
            </Text>
            <HStack spacing={2}>
              <Text fontSize="micro" opacity="secondary">
                {aiExtraTokenLimit > 0
                  ? `${formatTokenCount(aiExtraTokensUsed)} / ${formatTokenCount(aiExtraTokenLimit)}`
                  : '0'}
              </Text>
              <GradientTooltip
                label={
                  <Text fontSize="micro" whiteSpace="nowrap">
                    {hasTokens ? 'Adds 50K one-time AI tokens. Costs 1 BigToken.' : 'Get BigTokens to extend AI limit.'}
                  </Text>
                }
                placement="bottom"
              >
                <Box>
                  <RedeemButton
                    label="+50K"
                    onClick={() => {
                      if (!hasTokens) {
                        onOpen();
                        return;
                      }
                      redeemMutation.mutate({ rewardType: 'extend-ai-limit' });
                    }}
                    isLoading={
                      redeemMutation.isPending &&
                      redeemMutation.variables?.rewardType === 'extend-ai-limit'
                    }
                  />
                </Box>
              </GradientTooltip>
            </HStack>
          </HStack>
          <UsageBar value={aiExtraPercentUsed} color={aiExtraColor} />
          <Text fontSize="micro" opacity="tertiary" mt={1.5}>
            Spent first. Never resets.
          </Text>
        </Box>
      )}

      {/* File Storage */}
      {!isTerminated && (
        <Box
          width="100%"
          p={3}
          borderRadius="lg"
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
        >
          <HStack justifyContent="space-between" mb={2}>
            <Text fontSize="small" fontWeight="semibold">
              Storage
            </Text>
            <HStack spacing={2}>
              <Text fontSize="micro" opacity="secondary">
                {formatBytes(storageUsedBytes)} / {formatBytes(storageLimitBytes)}
              </Text>
              <GradientTooltip
                label={
                  <Text fontSize="micro" whiteSpace="nowrap">
                    {hasTokens ? 'Adds 5 MB permanent storage. Costs 1 BigToken.' : 'Get BigTokens to expand storage.'}
                  </Text>
                }
                placement="bottom"
              >
                <Box>
                  <RedeemButton
                    label="+5 MB"
                    onClick={() => {
                      if (!hasTokens) {
                        onOpen();
                        return;
                      }
                      redeemMutation.mutate({ rewardType: 'increase-storage' });
                    }}
                    isLoading={
                      redeemMutation.isPending &&
                      redeemMutation.variables?.rewardType === 'increase-storage'
                    }
                  />
                </Box>
              </GradientTooltip>
            </HStack>
          </HStack>
          <UsageBar value={storagePercentUsed} color={storageColor} />
          <Text fontSize="micro" opacity="tertiary" mt={1.5}>
            {formatBytes(storageLimitBytes)} permanent.
          </Text>
        </Box>
      )}

      {/* Next recharge date */}
      {!isTerminated && (
        <Box
          width="100%"
          p={3}
          borderRadius="lg"
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
        >
          <HStack spacing={1.5}>
            <Text fontSize="small" fontWeight="semibold">
              Next Recharge
            </Text>
            <Text fontSize="micro" opacity="secondary">
              {format(new Date(nextTokenRecycleTime), DATE_FORMAT)}
            </Text>
          </HStack>
        </Box>
      )}

      <Button
        fullWidth
        scheme="gradient"
        onClick={onOpen}
        leftIcon={<Icon type="Bigtokens" size={20} />}
      >
        Get Extra BigTokens
      </Button>
      <TokensModal isOpen={isOpen} onClose={onClose} />
    </VStack>
  );

  const renderTooltipContent = () => (
    <VStack spacing={3} width="100%">
      {/* Tab switcher — hidden for legacy free users (no AI/Storage access) */}
      {!isLegacy && (
        <Box
          width="100%"
          p={1}
          bg="whiteAlpha.100"
          borderRadius="lg"
          display="flex"
          gap={1}
        >
          <TabButton
            label="BigTokens"
            icon={<Icon type="Bigtokens" size={16} />}
            isActive={activeTab === 0}
            onClick={() => setActiveTab(0)}
          />
          <TabButton
            label="Usage"
            icon={<Icon type="GenerateCodeSolution" size={16} />}
            isActive={activeTab === 1}
            onClick={() => setActiveTab(1)}
          />
        </Box>
      )}

      {/* Tab content — legacy users always see tokens tab only */}
      {isLegacy || activeTab === 0 ? renderTokensTab() : renderUsageTab()}
    </VStack>
  );

  const triggerComponent = () => {
    return (
      <Box
        p="3px"
        background="linear-gradient(360deg, rgba(159, 122, 234, 0.57) 50.16%, rgba(237, 137, 54, 0.87) 95.35%)"
        borderRadius="50px"
      >
        <Box
          py={1}
          px={3}
          background="linear-gradient(360deg, rgba(159, 122, 234, 0.87) 1.16%, rgba(237, 137, 54, 0.87) 95.35%);"
          borderRadius="50px"
        >
          <Stack justifyContent="center" alignItems="center" direction="row" spacing={1.5}>
            <ProjectIcon type="Bigtokens" size="sm" />
            <HStack spacing={1}>
              <Text>{tokensAvailable}</Text>
              {extraTokens > 0 && <Text opacity="secondary">(+{extraTokens})</Text>}
            </HStack>
          </Stack>
        </Box>
      </Box>
    );
  };

  return (
    <GradientPopover header={null} triggerComponent={triggerComponent()} isHover>
      {renderTooltipContent()}
    </GradientPopover>
  );
};

export default UserBigtokens;
